import React, { useCallback, useMemo, useState } from "react";
import { Modal, Button, Form, Input, Select, DatePicker } from "antd";
import { useTranslation } from "react-i18next";
import axios, { AxiosError, AxiosResponse } from "axios";
import { EditOutlined } from "@ant-design/icons";
import * as Yup from "yup";
import TextArea from "antd/lib/input/TextArea";
import { DefaultOptionType } from "antd/lib/select";
import moment from "moment";
import api from "../../../services/api";
import { useToast } from "../../../hooks/toast";
import FormItem from "../../FormItem";
import { IDetailedProject } from "../../../pages/Project";
import getValidationErrors from "../../../utils/getValidationErrors";
import { useAuth } from "../../../hooks/auth";
import UserRoles from "../../../enums/UserRoles";

interface IEditFieldModalProps {
  project: IDetailedProject;
  fieldName: string;
  handleUpdateProject: (project: IDetailedProject) => void;
  validationRule?: Yup.StringSchema<string>;
  inputType?: "INPUT" | "TEXTAREA" | "SELECT" | "DATE";
  selectOptions?: DefaultOptionType[];
}

interface IEditFieldModalFormData {
  [key: string]: string;
}

interface IApprovalData {
  status: string;
  reason: string;
}

const layout = {
  labelCol: { span: 4 },
  wrapperCol: { span: 20 },
};

const EditFieldModal: React.FC<IEditFieldModalProps> = ({
  project,
  fieldName,
  handleUpdateProject,
  validationRule,
  inputType = "INPUT",
  selectOptions = [],
}) => {
  const { t } = useTranslation(["project-actions"]);
  const [visible, setVisible] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [validationErrors, setValidationErrors] = useState({});

  const [form] = Form.useForm();
  const { user } = useAuth();

  const { addToast } = useToast();

  const isLoggedUserManager = useMemo(
    () =>
      user.role === UserRoles.Manager || user.role === UserRoles.Administrative,
    [user.role]
  );

  const handleClose = useCallback(() => {
    setVisible(false);
  }, []);

  const generateErrorToast = useCallback(
    (response: AxiosResponse | undefined) => {
      let description;

      switch (response?.status) {
        case 404:
          description = "O projeto não foi encontrado";
          break;
        default:
          description = "Um erro occoreu ao alterar o campo, tente novamente";
      }

      addToast({
        type: "error",
        title: "Erro ao cancelar",
        description,
      });
    },
    [addToast]
  );

  const handleSendRequest = useCallback(
    async (data: IEditFieldModalFormData) => {
      try {
        setLoading(true);

        const response = await api.patch(`/projects/${project.id}`, data);
        handleUpdateProject(response.data);

        addToast({
          type: "success",
          title: "Campo alterado",
          description: "O projeto foi atualizado com sucesso",
        });
      } catch (err) {
        if (axios.isAxiosError(err)) {
          const serverError = err as AxiosError;
          generateErrorToast(serverError.response);
        } else {
          console.log(err);
        }
      } finally {
        setLoading(false);
        handleClose();
      }
    },
    [addToast, generateErrorToast, handleClose, handleUpdateProject, project.id]
  );

  const handleSubmit = useCallback(
    async (data: IEditFieldModalFormData) => {
      try {
        if (validationRule) {
          const shape = {} as any;
          shape[fieldName] = validationRule;
          const schema = Yup.object().shape(shape);

          await schema.validate(data, {
            abortEarly: false,
          });
        }
        handleSendRequest(data);
      } catch (err) {
        if (err instanceof Yup.ValidationError) {
          const errors = getValidationErrors(err);
          setValidationErrors(errors);
        }
      }
    },
    [fieldName, handleSendRequest, validationRule]
  );

  return isLoggedUserManager ? (
    <>
      <Modal
        // title={t("cancel")}
        title={`${t("edit_field")} ${t(`${fieldName}`, { ns: "projects" })}`}
        visible={visible}
        onOk={() => form.submit()}
        onCancel={() => handleClose()}
        okButtonProps={{ htmlType: "submit", loading }}
        getContainer={false}
        forceRender
      >
        <Form
          {...layout}
          form={form}
          name="edit-field-form"
          onFinish={handleSubmit}
        >
          {inputType === "INPUT" && (
            <FormItem
              name={fieldName}
              label={t("value")}
              errors={validationErrors}
            >
              <Input
                defaultValue={`${project[fieldName as keyof IDetailedProject] || ""}`}
              />
            </FormItem>
          )}

          {inputType === "TEXTAREA" && (
            <FormItem
              name={fieldName}
              label={t("value")}
              errors={validationErrors}
            >
              <TextArea
                rows={10}
                defaultValue={`${project[fieldName as keyof IDetailedProject] || ""}`}
              />
            </FormItem>
          )}

          {inputType === "SELECT" && (
            <FormItem
              name={fieldName}
              label={t("value")}
              errors={validationErrors}
            >
              <Select
                allowClear
                defaultValue={`${project[fieldName as keyof IDetailedProject]}`}
                style={{ width: "100%" }}
                options={selectOptions}
              />
            </FormItem>
          )}

          {inputType === "DATE" && (
            <FormItem
              name={fieldName}
              label={t("value")}
              errors={validationErrors}
            >
              <DatePicker
                style={{ width: "100%" }}
                defaultValue={
                  moment(
                    `${project[fieldName as keyof IDetailedProject]}`
                  ).isValid()
                    ? moment(`${project[fieldName as keyof IDetailedProject]}`)
                    : undefined
                }
                format="DD/MM/YYYY"
              />
            </FormItem>
          )}
        </Form>
      </Modal>
      <Button
        type="link"
        shape="circle"
        icon={<EditOutlined />}
        onClick={() => setVisible((oldVisible) => !oldVisible)}
      />
    </>
  ) : (
    <></>
  );
};

export default EditFieldModal;
